<template>
  <div>
    <h3 class="mt-4">
      Klausel-Vergleich
    </h3>
    <u-error-handling
      v-if="errorObject"
      :error-object="errorObject"
    />
    <div
      v-if="isLoading"
      style="text-align: center"
    >
      {{ maxCriterions - criterionsToWaitFor }}/{{ maxCriterions }} Klauseln
      geladen
      <v-progress-linear
        :value="((maxCriterions - criterionsToWaitFor) / maxCriterions) * 100"
        class="w-50"
        style="left: 25%"
      />
    </div>
    <table
      v-else
      class="table comparisonTable"
    >
      <thead>
        <tr>
          <td />
          <td>{{ contractOneShortName }}</td>
          <td>{{ contractTwoShortName }}</td>
          <td>Vergleich</td>
        </tr>
      </thead>
      <tbody :key="selectedLanguage">
        <template v-for="(topLevel, topLevelIndex) in criterionStructure">
          <!-- Top level row -->
          <tr
            :key="`top-${topLevelIndex}-${topLevel.path}`"
            class="topLevelConfig"
          >
            <td colspan="4">
              {{ topLevel.value }}
            </td>
          </tr>

          <!-- Second level items -->
          <template
            v-for="(secondLevel, secondLevelIndex) in topLevel.subLevels"
          >
            <tr
              :key="`second-${topLevelIndex}-${secondLevelIndex}-${
                secondLevel.path
              }`"
              class="secondLevelConfig"
            >
              <td colspan="4">
                {{ secondLevel.value }}
              </td>
            </tr>

            <!-- Criterion items -->
            <ComparableTableRow
              v-for="(criterion, criterionIndex) in secondLevel.criterions"
              :key="`criterion-${topLevelIndex}-${secondLevelIndex}-${criterionIndex}-${
                criterion.typeId
              }`"
              :order="
                topLevelIndex * 1000000 +
                  secondLevelIndex * 1000 +
                  criterionIndex
              "
              :text1="
                GetCriterionValue(
                  contractOneInformation.editionId,
                  criterion.typeId,
                )
              "
              :text2="
                GetCriterionValue(
                  contractTwoInformation.editionId,
                  criterion.typeId,
                )
              "
              :title="criterion.value"
              @comparisonFinished="
                (comparison) => {
                  addComparison(comparison);
                }
              "
            />
          </template>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import ComparableTableRow from "@/components/compare/ComparableTableRow.vue";
import { LoadAllCriterionConfigurations } from "@/services/criterion.management.service.js";
import { GetCriterion } from "@/services/contract.edition.criterion.service.js";

export default {
  components: {
    ComparableTableRow,
  },
  props: {
    contractOneShortName: {
      type: String,
      default: "",
    },
    selectedLanguage: {
      type: String,
      default: "de-CH",
    },
    contractTwoShortName: {
      type: String,
      default: "",
    },
    contractOneInformation: {
      type: Object,
      required: true,
      validator: function (obj) {
        return (
          obj.hasOwnProperty("contractId") &&
          obj.hasOwnProperty("versionId") &&
          obj.hasOwnProperty("editionId")
        );
      },
    },
    contractTwoInformation: {
      type: Object,
      required: true,
      validator: function (obj) {
        return (
          obj.hasOwnProperty("contractId") &&
          obj.hasOwnProperty("versionId") &&
          obj.hasOwnProperty("editionId")
        );
      },
    },
  },
  data() {
    return {
      isLoading: false,
      errorObject: null,
      allCriterionConfiguration: [],
      criterionStructure: [],
      loadedCriterions: [],
      maxCriterions: 0,
      criterionsToWaitFor: 0,
      criterionComparisons: [],
      waitForUpdate: 0,
    };
  },
  watch: {
    contractOneInformation: {
      handler(newVal) {
        this.validateContractInfo(newVal, "Contract One");
      },
      deep: true,
    },
    contractTwoInformation: {
      handler(newVal) {
        this.validateContractInfo(newVal, "Contract Two");
      },
      deep: true,
    },
  },
  created() {
    this.GetCriterionConfigurations();
  },
  methods: {
    addComparison(comparison) {
      // Log comparison data for debugging
      this.waitForUpdate = 0;
      const existingIndex = this.criterionComparisons.findIndex(
        (existing) => existing.order === comparison.order,
      );

      if (existingIndex >= 0) {
        this.criterionComparisons.splice(existingIndex, 1);
      }

      this.criterionComparisons.push(comparison);
      this.waitForUpdate++;

      setTimeout(() => {
        this.waitForUpdate--;
        if (this.waitForUpdate === 0) {
          this.criterionComparisons.sort((a, b) => a.order - b.order);
          this.$emit("criterionsLoaded", this.criterionComparisons);
        }
      }, 500);
    },
    GetCriterionValue(editionId, klauselTypeId) {
      if (!editionId || !klauselTypeId) {
        return "";
      }

      const criterion = this.loadedCriterions.find(
        (criterion) =>
          criterion.editionId === editionId &&
          criterion.typeId === klauselTypeId,
      );

      if (!criterion || !criterion.data || !criterion.data.klauselContents) {
        return "";
      }

      // Find content for current language
      const content = criterion.data.klauselContents.find(
        (content) => content.language === this.selectedLanguage,
      );

      if (content && content.value) {
        // Return the raw content without wrapper div
        return content.value;
      }

      // If no content found for selected language, try default language
      const defaultContent = criterion.data.klauselContents.find(
        (content) => content.language === "de-CH",
      );

      return defaultContent ? defaultContent.value : "";
    },
    GetCriterionConfigurations() {
      this.isLoading = true;

      LoadAllCriterionConfigurations(
        (response) => {
          this.allCriterionConfiguration = JSON.parse(JSON.stringify(response));

          this.allCriterionConfiguration.map((config) => {
            config.isLoading = true;
          });

          this.criterionStructure = [];

          this.allCriterionConfiguration
            .filter((c) => {
              return (c.path.match(/\//g) || []).length == 1;
            })
            .map((config) => {
              var level = {
                path: config.path,
                value: config.value,
                subLevels: [],
              };

              this.allCriterionConfiguration
                .filter((sublevel) => {
                  return (
                    (sublevel.path.match(/\//g) || []).length == 2 &&
                    sublevel.path.indexOf(config.path) == 0
                  );
                })
                .map((subLevelConfig) => {
                  var subLevel = {
                    path: subLevelConfig.path,
                    value: subLevelConfig.value,
                    criterions: [],
                  };

                  this.allCriterionConfiguration
                    .filter((criterion) => {
                      return (
                        (criterion.path.match(/\//g) || []).length == 3 &&
                        criterion.path.indexOf(subLevelConfig.path) == 0
                      );
                    })
                    .map((criterionConfig) => {
                      var criterion = {
                        path: criterionConfig.path,
                        value: criterionConfig.value,
                        typeId: criterionConfig.klauselTypeId,
                      };

                      subLevel.criterions.push(criterion);
                    });

                  level.subLevels.push(subLevel);
                });

              this.criterionStructure.push(level);
            });

          this.loadCriterionsForContracts();
        },
        () => {},
      );
    },

    loadCriterionsForContracts() {
      // Reset error state
      this.errorObject = null;

      // Validate both contracts
      const isContractOneValid = this.validateContractInfo(
        this.contractOneInformation,
        "Contract One",
      );
      const isContractTwoValid = this.validateContractInfo(
        this.contractTwoInformation,
        "Contract Two",
      );

      if (!isContractOneValid || !isContractTwoValid) {
        const errorMessage = [
          "Invalid contract information provided.",
          !isContractOneValid
            ? "Contract One has missing or invalid fields."
            : "",
          !isContractTwoValid
            ? "Contract Two has missing or invalid fields."
            : "",
        ]
          .filter(Boolean)
          .join(" ");

        console.error(errorMessage);
        this.isLoading = false;
        return;
      }

      // Proceed with criterion loading if validation passes
      var allCriterions = [];

      this.criterionStructure.map((topLevel) => {
        topLevel.subLevels.map((subLevel) => {
          subLevel.criterions.map((criterion) => {
            allCriterions.push(criterion);
          });
        });
      });

      this.criterionsToWaitFor = 2 * allCriterions.length;
      this.maxCriterions = this.criterionsToWaitFor;
      this.loadedCriterions = [];

      // Add try-catch block around criterion loading
      try {
        allCriterions.forEach((criterion) => {
          this.loadCriterion(criterion.typeId, this.contractOneInformation);
          this.loadCriterion(criterion.typeId, this.contractTwoInformation);
        });
      } catch (error) {
        console.error("Error loading criterions:", error);
        this.errorObject = error;
        this.isLoading = false;
      }
    },
    loadCriterion(typeId, contractInfo) {
      if (!typeId) {
        console.error("Missing typeId for criterion");
        this.criterionsToWaitFor--;
        return;
      }

      if (!this.validateContractInfo(contractInfo)) {
        console.error("Invalid contract info for criterion:", typeId);
        this.criterionsToWaitFor--;
        return;
      }

      GetCriterion(
        contractInfo.contractId,
        contractInfo.versionId,
        contractInfo.editionId,
        typeId,
        (success) => {
          if (!success) {
            console.warn("No data returned for criterion:", typeId);
          }

          // Ensure data has the expected structure
          const processedData = success || { klauselContents: [] };

          // Add the criterion to loadedCriterions
          this.loadedCriterions.push({
            editionId: contractInfo.editionId,
            typeId: typeId,
            data: processedData,
          });

          this.criterionsToWaitFor--;
          this.checkLoadingComplete();
        },
        (error) => {
          console.error("Error loading criterion:", typeId, error);
          this.criterionsToWaitFor--;
          this.checkLoadingComplete();
        },
      );
    },
    checkLoadingComplete() {
      if (this.criterionsToWaitFor === 0) {
        this.isLoading = false;
        this.criterionComparisons = this.criterionComparisons.sort((a, b) => {
          return a.order - b.order;
        });
        this.$emit("criterionsLoaded", this.criterionComparisons);
      }
    },
    validateContractInfo(contractInfo, label = "") {
      if (!contractInfo) {
        console.error(`${label} Contract info is null or undefined`);
        return false;
      }

      const requiredFields = {
        contractId: contractInfo.contractId,
        versionId: contractInfo.versionId,
        editionId: contractInfo.editionId,
      };

      const missingFields = Object.entries(requiredFields)
        .filter(([, value]) => value === undefined || value === null) // Remove underscore, just use comma
        .map(([key]) => key);

      if (missingFields.length > 0) {
        console.error(`${label} Missing required fields:`, missingFields);
        return false;
      }

      return true;
    },
  },
};
</script>

<style>
.comparisonTable .nonave {
  background-color: rgba(41, 205, 255, 0.432);
}

.comparisonTable .nonpvl {
  background-color: rgba(248, 248, 0, 0.377);
}

.comparisonTable .smartcom {
  background-color: rgba(255, 0, 14, 0.327);
}

.comparisonTable .tempSmartCom {
  background-color: rgba(255, 0, 14, 0.327);
}
</style>

<style scoped>
.comparisonTable {
  table-layout: fixed;
}

.comparisonTable td:nth-child(1) {
  width: 10%;
}

.comparisonTable td:nth-child(2) {
  width: 30%;
}

.comparisonTable td:nth-child(3) {
  width: 30%;
}

.comparisonTable td:nth-child(4) {
  width: 30%;
}

.comparisonTable td:nth-child(1),
.comparisonTable thead {
  font-weight: bold;
}

.comparisonTable td:nth-child(2) {
  background-color: rgb(245, 245, 245);
}

.comparisonTable td:nth-child(4) {
  border-left: solid gainsboro(211, 209, 209) 2px;
}

.comparisonTable td:nth-child(1),
.comparisonTable td:nth-child(2),
.comparisonTable td:nth-child(3) {
  border-right: solid gainsboro 1px;
}

.comparisonTable tr.newSection {
  border-top: solid gray 2px;
}

.comparisonTable thead td {
  border-top: none;
}

.comparisonTable ins {
  margin-left: 2px;
}

.comparisonTable .switchSymbol {
  color: gray;
  margin-left: 50%;
  font-size: 20px;
  font-weight: bold;
}

.topLevelConfig td:nth-child(1),
.secondLevelConfig td:nth-child(1) {
  text-align: center;
  border-right: solid white 1px;
}

.topLevelConfig td {
  font-size: 1.8rem;
}

.secondLevelConfig td {
  font-size: 1.2rem;
}
</style>
